import React, { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  getEditAccountType,
  getIsAccountTypeModalOpen,
  toggleAccountTypeModal,
} from "../../features/accounts/accountTypesSlice";

import styles from "./AccountTypeFormModal.module.scss";
import { IFormInput } from "../../types/accountTypes";

type Props = {
  handleSaveAccountType: (data: IFormInput) => void;
};

const AccountTypeFormModal = ({ handleSaveAccountType }: Props) => {
  const dispatch = useAppDispatch();
  const editAccountType = useAppSelector(getEditAccountType);
  const isAccountTypeModalOpen = useAppSelector(getIsAccountTypeModalOpen);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const {
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm<IFormInput>({
    defaultValues: { name: "", phone: "", description: "" },
  });

  const handleOnSubmit: SubmitHandler<IFormInput> = async (data: any) => {
    setIsLoading(true);
    await handleSaveAccountType(data);
    handleCloseFormModal();
    setIsLoading(false);
  };

  const handleCloseFormModal = () => {
    reset();
    dispatch(toggleAccountTypeModal(false));
  };

  useEffect(() => {
    if (editAccountType && editAccountType.id) {
      reset({
        name: editAccountType.name,
        phone: editAccountType.phone,
        description: editAccountType.description,
      });
    } else {
      reset({ name: "", phone: "", description: "" });
    }
  }, [editAccountType, isAccountTypeModalOpen]);

  return (
    <>
      {isAccountTypeModalOpen === true && (
        <div className={styles.AccountTypeFormModal}>
          <div className="relative h-[100vh] w-[100vw] overflow-y-scroll lg:overflow-auto top-0 lg:top-10 lg:mx-auto p-5 border lg:w-96 lg:h-fit shadow-lg lg:rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-2xl text-center font-bold leading-6 text-primary-1">
                એકાઉન્ટ ટાઈપ ની માહિતી
              </h3>
              <div className="mt-2 py-3">
                <form onSubmit={handleSubmit(handleOnSubmit)}>
                  <div className="grid gap-4">
                    <div className="mt-6">
                      <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-16 lg:h-14 w-full relative rounded">
                        <input
                          className="text-primary-1 focus:outline-none px-3 py-1 font-semibold text-xl placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                          type="text"
                          placeholder="એકાઉન્ટ ટાઈપનુ નામ દાખલ કરો"
                          {...register("name", { required: true })}
                        />
                      </div>
                      {errors.name && errors.name.type === "required" && (
                        <span className="text-red-500">
                          એકાઉન્ટ ટાઈપનુ નામ દાખલ કરો
                        </span>
                      )}
                    </div>

                    <div className="mt-3">
                      <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-16 lg:h-14 w-full relative rounded">
                        <input
                          className="text-primary-1 focus:outline-none px-3 py-1 font-semibold text-xl placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                          type="text"
                          placeholder="મોબાઇલ નંબર દાખલ કરો"
                          {...register("phone", { required: false })}
                        />
                      </div>
                      {errors.phone && errors.phone.type === "required" && (
                        <span className="text-red-500">
                          મોબાઇલ નંબર દાખલ કરો
                        </span>
                      )}
                    </div>

                    <div className="mt-3">
                      <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-28 w-full relative rounded">
                        <textarea
                          rows={5}
                          className="text-primary-1 focus:outline-none px-3 py-1 font-semibold text-xl placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                          placeholder="એકાઉન્ટ ટાઈપનુ ડિસ્ક્રિપ્શન દાખલ કરો"
                          {...register("description", { required: false })}
                        />
                      </div>
                      {errors.description &&
                        errors.description.type === "required" && (
                          <span className="text-red-500">
                            એકાઉન્ટ ટાઈપનુ ડિસ્ક્રિપ્શન દાખલ કરો
                          </span>
                        )}
                    </div>

                    <div className="mt-3 text-center">
                      <button
                        className="mx-auto shadow disabled:opacity-50 rounded-3xl text-3xl lg:text-xl text-white bg-gradient-to-r from-primary-1 to-primary-2 font-bold px-10 py-2"
                        type="submit"
                        disabled={isLoading}
                      >
                        {isLoading ? "રાહ જુઓ..." : "આગળ વધો"}
                      </button>
                    </div>

                    <div className="mt-1 text-center">
                      <button
                        className="mx-auto shadow disabled:opacity-50 rounded-3xl text-3xl lg:text-xl text-white bg-gradient-to-r from-primary-1 to-primary-2 font-bold px-10 py-2"
                        type="button"
                        onClick={handleCloseFormModal}
                        disabled={isLoading}
                      >
                        બંધ કરો
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AccountTypeFormModal;
